import { useEffect, useState } from 'react'

import type { Fix } from '../map/userLocationLayer'
import { loadPersistedFix, savePersistedFix } from './persistedFix'

/**
 * `navigator.geolocation.watchPosition` as a React hook.
 *
 * Research §Code Examples Pattern 4 (lines 422-472). Decisions consumed:
 *   - D-71: prompt on load — `watchPosition` triggers the native dialog.
 *   - D-72: seed initial fix from persisted localStorage → Bucharest
 *     handled at the MapView layer.
 *   - D-73: continuous `watchPosition` — subscribe on mount, clearWatch on
 *     unmount.
 *   - D-74: persist fixes whose accuracy < 500m.
 *   - D-76: `perm === 'denied'` drives the PermissionBanner.
 *
 * Threat T-2-GEO: all three `GeolocationPositionError.code` values are
 * handled without crashing — PERMISSION_DENIED (1) flips `perm='denied'`;
 * POSITION_UNAVAILABLE (2) and TIMEOUT (3) surface via `err` but leave
 * `perm` untouched (they are not a denial).
 *
 * Pitfall 3: `navigator.permissions.query` is missing/inconsistent on iOS
 * Safari standalone PWAs; we swallow errors from that path and rely on
 * the `watchPosition` error callback as authoritative.
 */

export type PermState = 'granted' | 'denied' | 'prompt' | 'unknown'

export interface GeoState {
  fix: Fix | null
  perm: PermState
  err: GeolocationPositionError | null
}

export function useGeolocation(): GeoState {
  // Seed initial fix from persisted localStorage so the first paint can
  // already center on the last known position while the browser permission
  // dialog is still pending (D-72 step 1).
  const [fix, setFix] = useState<Fix | null>(() => loadPersistedFix())
  const [perm, setPerm] = useState<PermState>('unknown')
  const [err, setErr] = useState<GeolocationPositionError | null>(null)

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setPerm('denied')
      return
    }

    // Permissions API is best-effort. Safari iOS standalone can throw or
    // report 'prompt' when the user has actually denied — we don't trust it
    // as authoritative (Pitfall 3). Swallow errors.
    const permissions = (navigator as Navigator).permissions
    if (permissions && typeof permissions.query === 'function') {
      try {
        permissions
          .query({ name: 'geolocation' as PermissionName })
          .then((status) => {
            setPerm(status.state as PermState)
            status.onchange = () => setPerm(status.state as PermState)
          })
          .catch(() => {
            /* leave perm untouched */
          })
      } catch {
        /* some browsers throw synchronously — ignore */
      }
    }

    // Capture geo ref at subscribe time so the cleanup path survives
    // global navigator un-stubbing by the test harness afterEach.
    const geo = navigator.geolocation
    const id = geo.watchPosition(
      (pos) => {
        const next: Fix = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          ts: pos.timestamp,
        }
        setFix(next)
        setPerm('granted')
        setErr(null)
        if (next.accuracy < 500) savePersistedFix(next)
      },
      (e) => {
        setErr(e)
        // Only PERMISSION_DENIED flips perm. POSITION_UNAVAILABLE and
        // TIMEOUT are transient upstream errors, not denials.
        if (e.code === 1 /* PERMISSION_DENIED */) setPerm('denied')
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 },
    )

    return () => {
      // Use the captured geo ref — navigator may have been replaced
      // between mount and unmount (e.g. by vi.unstubAllGlobals in tests),
      // and optional-chain in case geo was never valid.
      geo?.clearWatch?.(id)
    }
  }, [])

  return { fix, perm, err }
}
